/**
 * Pure decision logic for the router-wide error boundary (see `route-error.tsx`).
 *
 * Kept in its own dependency-free module so the branch logic is unit-testable without pulling in
 * `./main` (the tRPC client / persister, whose import isn't node-safe) or a router/DOM. The effect
 * and fallback UI this drives are covered by the redirect e2e tests.
 */

// The route-policy prefix sets are the SAME ones the proactive `_app` layout guard uses (route-access) —
// shared so the reactive boundary net and the guard can never drift apart.
import { AUTH_REQUIRED_ROUTE_PREFIXES, TEST_GATED_ROUTE_PREFIXES } from './route-access'

// The cached `getUser` shape the classifier reads (authOptional → null when logged out).
export interface RouteErrorUser {
  type?: string
  testPassed?: boolean | null
  testPassedValues?: boolean | null
  testPassedPersonality?: boolean | null
}

/**
 * Did a CODE chunk fail to load (a dynamic `import()` of a route/lazy component)?
 *
 * This needs its own branch because the browser CACHES the rejection: re-importing the same URL returns the
 * cached failure, so re-rendering or refetching can never recover it — only a full reload re-fetches. And
 * because it's usually not a bug: hashed assets persist on the CDN (upload-to-cdn.ts), so the common cause is
 * the network being down, which is why the boundary waits for reachability instead of reloading blind.
 *
 * Matches the dynamic-import wordings ONLY, and deliberately NOT a bare "Failed to fetch" — that is also what a
 * failed tRPC DATA query throws offline, and a data error must recover in place, not by reloading the document.
 * Each engine words it differently: Chrome "Failed to fetch dynamically imported module", Safari "Importing a
 * module script failed", Firefox "error loading dynamically imported module"; bundlers add ChunkLoadError.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : ''
  return /dynamically imported module|imported module script failed|importing a module script failed|ChunkLoadError|Loading chunk/i.test(
    message,
  )
}

/**
 * Given the active route, the cached user and the thrown message, decide whether the boundary should
 * redirect to login (`isAuth`) or to the unfinished test (`isTestIncomplete`).
 *
 *  - No cached user on an auth-required route → auth/expired (key off the absent session, NOT the error
 *    code: a DEFERRED-streamed query's rejection has its code REDACTED in production).
 *  - A logged-in applicant with the test still pending, on a test-gated route → resume the test (same
 *    reason: the streamed message is often redacted). An explicit TEST_INCOMPLETE message also counts.
 */
export function classifyRouteError(input: {
  routeId: string
  cachedUser: RouteErrorUser | null
  message?: string | undefined
}): { isAuth: boolean; isTestIncomplete: boolean } {
  const { routeId, cachedUser, message } = input
  const hasUser = cachedUser != null
  const needsAuth = AUTH_REQUIRED_ROUTE_PREFIXES.some((p) => routeId.startsWith(p))
  const isAuth = !hasUser && needsAuth
  const applicantTestPending =
    cachedUser?.type === 'applicant' &&
    !(cachedUser.testPassed && cachedUser.testPassedValues && cachedUser.testPassedPersonality)
  const onTestGatedRoute = TEST_GATED_ROUTE_PREFIXES.some((p) => routeId.startsWith(p))
  const isTestIncomplete = message === 'TEST_INCOMPLETE' || (applicantTestPending && onTestGatedRoute)
  return { isAuth, isTestIncomplete }
}
